import axios from 'axios';
import { isNative, getPlatform } from './platform';

let pushModule = null;

// Dernier jeton émis par FCM/APNs pour cet appareil, et dernier couple
// (utilisateur, jeton) réellement transmis au serveur.
let deviceToken = null;
let lastSentSignature = null;

async function loadPush() {
  if (pushModule) return pushModule;
  const mod = await import('@capacitor/push-notifications');
  pushModule = mod.PushNotifications;
  return pushModule;
}

function currentUserId(store) {
  return store?.state?.auth?.user?.data?.id ?? null;
}

/**
 * Transmet le jeton au serveur dès que l'utilisateur ET le jeton sont connus.
 *
 * L'implémentation précédente n'envoyait le jeton que dans le gestionnaire
 * `registration`, en abandonnant si aucun utilisateur n'était connecté à cet
 * instant. Or `registration` se déclenche quelques centaines de millisecondes
 * après le lancement, donc bien avant que quiconque ait pu se connecter : à la
 * première installation, le jeton était systématiquement perdu et l'appareil ne
 * recevait jamais de notification. Il fallait attendre un relancement ultérieur
 * dans la fenêtre de session de 24 h pour que le jeton parte enfin.
 *
 * On retient donc le jeton et on le pousse à chaque fois que l'un des deux
 * éléments devient disponible, en évitant de renvoyer deux fois le même couple.
 */
async function sendDeviceToken(store) {
  const userId = currentUserId(store);
  if (!userId || !deviceToken) return;

  const signature = `${userId}:${deviceToken}`;
  if (signature === lastSentSignature) return;

  try {
    await axios.post('https://www.ouicsport.fr/api/user/device-token', {
      userId,
      token: deviceToken,
      platform: getPlatform(),
    });
    lastSentSignature = signature;
  } catch (e) {
    // On ne mémorise pas la signature en cas d'échec : la prochaine occasion
    // (nouvelle connexion, relancement) refera la tentative.
    console.warn('Enregistrement du jeton de notification échoué', e);
  }
}

export async function initPushNotifications(store, router) {
  if (!isNative()) return;

  const PushNotifications = await loadPush();

  const permStatus = await PushNotifications.checkPermissions();
  let permission = permStatus.receive;
  if (permission === 'prompt') {
    const req = await PushNotifications.requestPermissions();
    permission = req.receive;
  }
  if (permission !== 'granted') return;

  await PushNotifications.register();

  PushNotifications.addListener('registration', (tokenObj) => {
    deviceToken = tokenObj.value;
    sendDeviceToken(store);
  });

  // Rattrape le cas principal : le jeton arrive avant la connexion. Dès que
  // l'utilisateur se connecte, le couple est transmis.
  if (store && typeof store.watch === 'function') {
    store.watch(
      (state) => state.auth?.user?.data?.id ?? null,
      (userId) => {
        if (userId) sendDeviceToken(store);
      }
    );
  }

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const data = action?.notification?.data || {};
    if (data.offerId) {
      router.push({ name: 'OfferDetailsPage', params: { id: data.offerId } });
    } else if (data.path) {
      router.push(data.path);
    }
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.error('Push registration error', err);
  });
}
